// Layer 3 (Transformation): single `PeriodBalance` per accounting window — the
// only module on `/metal-accounting` that may read `windowSum`/`windowMean`,
// assay `rawValue`, or `RECONCILED_VALUES` (glass-poc-run A3 R6). Every other
// projection takes a `PeriodView` and never touches the series directly.

import type { AccountingPeriod, MeasuredQuantity } from '@/versions/v1/schema/metal-accounting';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { MEASUREMENTS, RECONCILED_VALUES } from '@/versions/v1/shared/bunker-hill-accounting-seed';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { seriesAt, windowMean } from '@/versions/v1/shared/series.derive';
import {
  diffMinutes,
  monthStart,
  productionDayOf,
  weekStart,
} from '@/versions/v1/shared/clock.derive';
import {
  juneMtdDays,
  juneWtdDays,
  type JuneTonnageDay,
} from '@/versions/v1/shared/june-tonnage.derive';
import { INITIAL_SESSION_STATE } from '@/versions/v1/schema/scenario';

export type ChainElement = 'Zn' | 'Pb' | 'Ag';

const UNMEASURED: MeasuredQuantity = { value: null, unit: 'unmeasured' };

const HEAD_GRADE_READING_ID: Record<ChainElement, string> = {
  Zn: 'rd-head-zn',
  Pb: 'rd-head-pb',
  Ag: 'rd-head-ag',
};

const RECOVERY_READING_ID: Record<ChainElement, string> = {
  Zn: 'rd-kpi-zn-rec',
  Pb: 'rd-kpi-pb-rec',
  Ag: 'rd-kpi-ag-rec',
};

const HEAD_GRADE_SERIES_ID: Record<ChainElement, string> = {
  Zn: 'rs-head-zn',
  Pb: 'rs-head-pb',
  Ag: 'rs-head-ag',
};

const RECOVERY_SERIES_ID: Record<ChainElement, string> = {
  Zn: 'rs-zn-rec',
  Pb: 'rs-pb-rec',
  Ag: 'rs-ag-rec',
};

const ORE_SERIES_ID = 'rs-ore-td';

/** The Pb concentrate grade series — the day's own assay, the per-day
 *  concentrate-mass weight the Ag basis averages over (#765). */
const PB_PRODUCT_GRADE_SERIES_ID = 'rs-pb-grade';

/** The Ag concentrate assay series behind SA03 (`rd-spec-ag-grade`). */
const AG_PRODUCT_GRADE_SERIES_ID = 'rs-ag-grade';

export const STAGE_OUTCOME_SERIES_ID: Record<string, string> = {
  'rd-pb-rgh-con-pb': 'rs-pb-rgh-con-pb',
  'rd-kpi-pb-grade': 'rs-pb-grade',
  'rd-zn-rgh-con-zn': 'rs-zn-rgh-con-zn',
  'rd-kpi-zn-grade': 'rs-zn-grade',
};

export function seriesPeriodFor(period: AccountingPeriod): 'day' | 'wtd' | 'mtd' | null {
  if (period === 'shift') return null;
  if (period === 'wtd') return 'wtd';
  if (period === 'mtd') return 'mtd';
  return 'day';
}

export function mtdWindowStart() {
  return monthStart(INITIAL_SESSION_STATE.cursor);
}

export function wtdWindowStart() {
  return weekStart(INITIAL_SESSION_STATE.cursor);
}

/** D25 / K17 round 1 (#663): Metal accounting's MTD window is the cursor
 *  month's **complete production days only** — the same read Planning
 *  publishes (`juneMtdDays`, the `completeProductionDays` filter in
 *  `shared/june-tonnage.derive.ts`). `to` is the open production day's stamp —
 *  the first day the window excludes — so every daily and shift series mean
 *  bounded by `[from, to)` covers exactly the days the tonnage sums.
 *
 *  Before D25 the MTD reads summed `rs-ore-td` from the month start with no
 *  upper bound, so the 8 Jun day still in progress (1,720 dst) counted whole:
 *  14,121 dst over 8 days, where Planning showed 12,401 dst over 1–7 Jun. */
export type MtdWindow = {
  /** Complete production days of the cursor month, oldest first. */
  days: readonly JuneTonnageDay[];
  /** Their summed ore tonnage (dst). */
  tonnes: number;
  /** Month start for daily series — 31 May 18:00 at the demo cursor. */
  from: SeriesTime;
  /** The open production day's stamp — 7 Jun 18:00 at the demo cursor. */
  to: SeriesTime;
};

export function mtdWindow(): MtdWindow {
  const days = juneMtdDays(INITIAL_SESSION_STATE);
  const last = days[days.length - 1];
  return {
    days,
    tonnes: days.reduce((sum, day) => sum + day.dst, 0),
    from: mtdWindowStart(),
    to: last ? last.closedAt : INITIAL_SESSION_STATE.cursor,
  };
}

/** D25 / K17 round 3 (#663), K21 (#719): Metal accounting's WTD window is the
 *  **complete production days inside the plant week** — `juneWtdDays`, the same
 *  shared function `/meeting` and the Overview read, and the six days `/cost`
 *  publishes (`COST_DAYS`, 2–7 Jun, 10,652 dst). `to` is the open production
 *  day's stamp, the first day the window excludes, so every daily and shift
 *  series mean bounded by `[from, to)` covers exactly the days the tonnage
 *  sums.
 *
 *  Before round 3 the WTD reads summed `rs-ore-td` from the week start with no
 *  upper bound, so the open 8 Jun day still in progress (1,720 dst) counted
 *  whole: 12,372 dst over 7 days, where `/cost` showed 10,652 dst over 2–7 Jun. */
export type WtdWindow = {
  /** Complete production days of the plant week, oldest first. */
  days: readonly JuneTonnageDay[];
  /** Their summed ore tonnage (dst). */
  tonnes: number;
  /** Week start for daily series — 1 Jun 18:00 at the demo cursor. */
  from: SeriesTime;
  /** The open production day's stamp — 7 Jun 18:00 at the demo cursor. */
  to: SeriesTime;
};

export function wtdWindow(): WtdWindow {
  const days = juneWtdDays(INITIAL_SESSION_STATE);
  const last = days[days.length - 1];
  return {
    days,
    tonnes: days.reduce((sum, day) => sum + day.dst, 0),
    from: wtdWindowStart(),
    to: last ? last.closedAt : INITIAL_SESSION_STATE.cursor,
  };
}

function parsePctFromAssay(text: string, element: string): number | null {
  const match = text.match(new RegExp(`([\\d.]+)%\\s*${element}`, 'i'));
  return match ? Number.parseFloat(match[1]) : null;
}

function parseOzPerTFromAssay(text: string): number | null {
  const match = text.match(/([\d.]+)\s*oz\/t/i);
  return match ? Number.parseFloat(match[1]) : null;
}

export type SampledAssays = {
  pbConcPct: number;
  agConcOzPerT: number;
  znConcPct: number;
  tailsPbPct: number;
  tailsZnPct: number;
  tailsAgOzPerT: number;
};

export function sampledAssays(): SampledAssays {
  const sa03 = MEASUREMENTS.find((entry) => entry.id === 'ms-sa03');
  const sa04 = MEASUREMENTS.find((entry) => entry.id === 'ms-sa04');
  const sa05 = MEASUREMENTS.find((entry) => entry.id === 'ms-sa05');
  return {
    pbConcPct: parsePctFromAssay(sa03?.rawValue ?? '', 'Pb') ?? 67.1,
    agConcOzPerT: parseOzPerTFromAssay(sa03?.rawValue ?? '') ?? 12.13,
    znConcPct: parsePctFromAssay(sa04?.rawValue ?? '', 'Zn') ?? 58.4,
    tailsPbPct: parsePctFromAssay(sa05?.rawValue ?? '', 'Pb') ?? 0.18,
    tailsZnPct: parsePctFromAssay(sa05?.rawValue ?? '', 'Zn') ?? 1.08,
    tailsAgOzPerT: parseOzPerTFromAssay(sa05?.rawValue ?? '') ?? 0.081,
  };
}

function znPbInZnConcPct(): number {
  return readingByIdLookup('rd-spec-zn-pb')?.value ?? 0;
}

function meteredFeedTonnes(period: 'day' | 'wtd' | 'mtd'): number | null {
  if (period === 'mtd') {
    // D25 / K17 round 1: complete production days of the cursor month.
    const ore = mtdWindow().tonnes;
    return Number.isFinite(ore) && ore > 0 ? ore : null;
  }
  if (period === 'wtd') {
    // D25 / K17 round 3: the week's complete production days only.
    const ore = wtdWindow().tonnes;
    return Number.isFinite(ore) && ore > 0 ? ore : null;
  }
  const ore = readingByIdLookup('rd-kpi-ore-td')?.value;
  return ore !== undefined && Number.isFinite(ore) ? ore : null;
}

export type OreWindowCoverage = {
  /** Daily ore points inside the window that carry a measured tonnage. */
  measuredDays: number;
  /** Days the window spans on the plant calendar — WTD and MTD are both the
   *  complete production days of their window plus any null-point day inside
   *  their span (D25 / K17 round 1 for MTD, round 3 for WTD: the open day is
   *  not a window day). `measuredDays < windowDays` means at least one day has
   *  no measured tonnage. */
  windowDays: number;
  /** Daily stamps inside the window whose seed point is null — a day with no
   *  measured tonnage, named rather than silently averaged over. */
  missingDays: readonly SeriesTime[];
};

/** K10b round 3 (#518): how a WTD/MTD window covers the daily ore series — the
 *  days a measured tonnage exists, the days the window spans, and any day
 *  stamped in the window with no measurement. Both the window tonnage
 *  (`meteredFeedTonnes`) and this count read the same `rs-ore-td` points, so the
 *  WTD/MTD feed rate divides the same measured tonnage by the days it actually
 *  has and never falls back to the typed design. Reads series directly — only
 *  this module may (A3 R6).
 *
 *  D25 / K17 round 1 (#663): the MTD window is the cursor month's complete
 *  production days (`mtdWindow`), not every point from the month start — so
 *  the open production day is neither counted nor named as a missing day.
 *
 *  D25 / K17 round 3 (#663): the WTD window is the plant week's complete
 *  production days (`wtdWindow`) on the same rule — the open day is not a
 *  window day and `windowDays` counts the window the tonnage sums, not the
 *  cursor's day-of-week. */
export function oreWindowCoverage(period: 'wtd' | 'mtd'): OreWindowCoverage {
  const window = period === 'mtd' ? mtdWindow() : wtdWindow();
  const missingDays = seriesAt(ORE_SERIES_ID, INITIAL_SESSION_STATE).points
    .filter(
      (point) =>
        point.value === null &&
        diffMinutes(point.at, window.from) >= 0 &&
        diffMinutes(point.at, window.to) < 0,
    )
    .map((point) => point.at);
  return {
    measuredDays: window.days.length,
    windowDays: window.days.length + missingDays.length,
    missingDays,
  };
}

function recoveryPctForSeriesPeriod(element: ChainElement, period: 'day' | 'wtd' | 'mtd'): number | null {
  const reading = readingByIdLookup(RECOVERY_READING_ID[element]);
  if (period === 'day') return reading?.value ?? null;
  // D25 / K17 round 3: both window means cover the same complete production
  // days their window tonnage sums — the open day is in neither.
  const window = period === 'mtd' ? mtdWindow() : wtdWindow();
  const mean = rangeMean(RECOVERY_SERIES_ID[element], window.from, window.to);
  return Number.isFinite(mean) ? mean : null;
}

function headGradeForSeriesPeriod(element: ChainElement, period: 'day' | 'wtd' | 'mtd'): number | null {
  if (period === 'day') return readingByIdLookup(HEAD_GRADE_READING_ID[element])?.value ?? null;
  const window = period === 'mtd' ? mtdWindow() : wtdWindow();
  const mean = rangeMean(HEAD_GRADE_SERIES_ID[element], window.from, window.to);
  return Number.isFinite(mean) ? mean : null;
}

/** The measured value of `seriesId` at exactly `at`, or `null`. Only this
 *  module reads the series directly (A3 R6). */
function pointValueAt(seriesId: string, at: SeriesTime): number | null {
  const point = seriesAt(seriesId, INITIAL_SESSION_STATE).points.find((entry) => entry.at === at);
  return point && point.value !== null && Number.isFinite(point.value) ? point.value : null;
}

/** F765 (#765): the reported Ag concentrate grade over a window is the **assayed**
 *  grade — the daily SA03 Ag assay series (`rs-ag-grade`) mass-weighted by each
 *  production day's Pb/Ag concentrate dmt. The day's concentrate mass is the same
 *  three-product identity the balance uses (`ore × head Pb × Pb recovery ÷ Pb
 *  concentrate grade`), read per production day so the weights are the window's
 *  own daily masses rather than one window average.
 *
 *  This is the metallurgical-reporting basis the issue asks for: the assayed
 *  concentrate grade is what the plant reports, and the balance's back-calculated
 *  grade is the closure check (`closureAgConcOzPerT`, never the reported figure).
 *
 *  Before #765 the WTD/MTD reported figure was that back-calculation, which never
 *  saw K20's `rs-ag-grade` re-base (row 279) and so read 11.41/11.24 oz/t against
 *  the day's assayed 12.14 — an MTD that needed the earlier June days near 11 and
 *  contradicted the case's six-shift fall. */
function assayWeightedAgGrade(period: 'wtd' | 'mtd'): number | null {
  const window = period === 'mtd' ? mtdWindow() : wtdWindow();
  let weighted = 0;
  let weight = 0;
  for (const day of window.days) {
    const headPb = pointValueAt(HEAD_GRADE_SERIES_ID.Pb, day.stamp);
    const recoveryPb = pointValueAt(RECOVERY_SERIES_ID.Pb, day.stamp);
    const pbGrade = pointValueAt(PB_PRODUCT_GRADE_SERIES_ID, day.stamp);
    const agGrade = pointValueAt(AG_PRODUCT_GRADE_SERIES_ID, day.stamp);
    if (headPb === null || recoveryPb === null || pbGrade === null || agGrade === null) continue;
    const concDmt = (day.dst * (headPb / 100) * (recoveryPb / 100)) / (pbGrade / 100);
    if (!Number.isFinite(concDmt) || concDmt <= 0) continue;
    weighted += concDmt * agGrade;
    weight += concDmt;
  }
  return weight > 0 ? weighted / weight : null;
}

export type PeriodBalance = {
  period: AccountingPeriod;
  feed: number;
  headGrade: Record<ChainElement, number>;
  recovery: Record<ChainElement, number>;
  productGrade: {
    pbConcPct: number;
    znConcPct: number;
    znPbInZnConcPct: number;
    /** The **reported** Ag concentrate grade — the assay basis (F765 / #765):
     *  the day's SA03 assay, and over WTD/MTD the assayed grade mass-weighted by
     *  each production day's Pb/Ag concentrate dmt. */
    agConcOzPerT: number;
  };
  /** The **back-calculated** Ag concentrate grade (`Ag metal in feed × recovery ÷
   *  C1`) — the closure's own Ag leg and nothing else (F765 / #765). It is what
   *  the closure gap and the recovery-uncertainty propagation read, so the
   *  balance still closes while the glass reports the assayed grade. */
  closureAgConcOzPerT: number;
  tailsAssay: {
    tailsPbPct: number;
    tailsZnPct: number;
    tailsAgOzPerT: number;
  };
  masses: { F: number; C1: number; C2: number; T: number };
  metalIn: Record<ChainElement, number>;
  metalOut: Record<ChainElement, number>;
  closureGapPct: { Pb: number; Zn: number; Ag: number };
};

export function periodBalance(period: AccountingPeriod): PeriodBalance | null {
  const sp = seriesPeriodFor(period);
  if (!sp) return null;

  const F = meteredFeedTonnes(sp);
  if (F === null) return null;

  const assays = sampledAssays();
  const znPb = znPbInZnConcPct();
  const headGrade: Record<ChainElement, number | null> = {
    Pb: headGradeForSeriesPeriod('Pb', sp),
    Zn: headGradeForSeriesPeriod('Zn', sp),
    Ag: headGradeForSeriesPeriod('Ag', sp),
  };
  const recovery: Record<ChainElement, number | null> = {
    Pb: recoveryPctForSeriesPeriod('Pb', sp),
    Zn: recoveryPctForSeriesPeriod('Zn', sp),
    Ag: recoveryPctForSeriesPeriod('Ag', sp),
  };
  if (
    headGrade.Pb === null ||
    headGrade.Zn === null ||
    headGrade.Ag === null ||
    recovery.Pb === null ||
    recovery.Zn === null ||
    recovery.Ag === null
  ) {
    return null;
  }

  const pbMetalInFeed = F * (headGrade.Pb / 100);
  const znMetalInFeed = F * (headGrade.Zn / 100);
  const agMetalInFeed = F * headGrade.Ag;
  const C1 = (pbMetalInFeed * (recovery.Pb / 100)) / (assays.pbConcPct / 100);
  const C2 = (znMetalInFeed * (recovery.Zn / 100)) / (assays.znConcPct / 100);
  const T = F - C1 - C2;
  if (T <= 0 || !Number.isFinite(C1) || !Number.isFinite(C2)) return null;

  // F765 (#765): the reported Ag concentrate grade is the assayed basis — the
  // day's SA03 assay, the window's dmt-weighted mean of the daily assays — and
  // the back-calculated grade is kept for the closure only. The WTD/MTD figure
  // used to be the back-calculation, which never saw K20's re-base (row 279) and
  // contradicted the day's assayed 12.14.
  const closureAgConcOzPerT = C1 > 0 ? (agMetalInFeed * (recovery.Ag / 100)) / C1 : assays.agConcOzPerT;
  const agConcOzPerT =
    sp === 'day' ? assays.agConcOzPerT : (assayWeightedAgGrade(sp) ?? closureAgConcOzPerT);

  const metalIn: Record<ChainElement, number> = {
    Pb: pbMetalInFeed,
    Zn: znMetalInFeed,
    Ag: agMetalInFeed,
  };
  const metalOut: Record<ChainElement, number> = {
    Pb: C1 * (assays.pbConcPct / 100) + C2 * (znPb / 100) + T * (assays.tailsPbPct / 100),
    Zn: C2 * (assays.znConcPct / 100) + T * (assays.tailsZnPct / 100),
    Ag: C1 * closureAgConcOzPerT + T * assays.tailsAgOzPerT,
  };

  const closureGapPct = {
    Pb: metalIn.Pb > 0 ? ((metalOut.Pb - metalIn.Pb) / metalIn.Pb) * 100 : 0,
    Zn: metalIn.Zn > 0 ? ((metalOut.Zn - metalIn.Zn) / metalIn.Zn) * 100 : 0,
    Ag: metalIn.Ag > 0 ? ((metalOut.Ag - metalIn.Ag) / metalIn.Ag) * 100 : 0,
  };

  return {
    period,
    feed: F,
    headGrade: { Pb: headGrade.Pb, Zn: headGrade.Zn, Ag: headGrade.Ag },
    recovery: { Pb: recovery.Pb, Zn: recovery.Zn, Ag: recovery.Ag },
    productGrade: {
      pbConcPct: assays.pbConcPct,
      znConcPct: assays.znConcPct,
      znPbInZnConcPct: znPb,
      agConcOzPerT,
    },
    closureAgConcOzPerT,
    tailsAssay: {
      tailsPbPct: assays.tailsPbPct,
      tailsZnPct: assays.tailsZnPct,
      tailsAgOzPerT: assays.tailsAgOzPerT,
    },
    masses: { F, C1, C2, T },
    metalIn,
    metalOut,
    closureGapPct,
  };
}

export type UnmeasuredPeriodView = {
  unmeasured: true;
  period: AccountingPeriod;
  reason: string;
};

export type PeriodView = PeriodBalance | UnmeasuredPeriodView;

export function periodView(period: AccountingPeriod): PeriodView {
  const balance = periodBalance(period);
  if (balance) return balance;
  return {
    unmeasured: true,
    period,
    reason: period === 'shift' ? 'No shift balance rows in seed' : 'Insufficient series for this period',
  };
}

export function isPeriodMeasured(view: PeriodView): view is PeriodBalance {
  return !('unmeasured' in view);
}

export type AccountingWindows = {
  shift: PeriodView;
  day: PeriodView;
  wtd: PeriodView;
  mtd: PeriodView;
};

/** All four tab views, built once in this module so downstream readers never
 *  call `periodView` / `periodBalance` themselves (R12). */
export function periodWindows(): AccountingWindows {
  return {
    shift: periodView('shift'),
    day: periodView('day'),
    wtd: periodView('wtd'),
    mtd: periodView('mtd'),
  };
}

export function productStreamMasses(
  period: 'day' | 'wtd' | 'mtd',
): { F: number; C1: number; C2: number; T: number } | null {
  const balance = periodBalance(period);
  return balance ? balance.masses : null;
}

/** Round to the on-glass mass precision (1 decimal dst). */
export function roundMass(value: number): number {
  return Math.round(value * 10) / 10;
}

/** Round to the on-glass grade precision (2 decimals). */
export function roundGrade(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Closure gap recomputed from displayed (rounded) table figures — R9.
 *
 *  K17 (#663): the Pb and Zn legs are the read Metal accounting's closure panel
 *  prints, and the band that tones it is evaluated on this same figure.
 *
 *  F1 (#727): the read carries all three chain elements, so Quality & Lab's
 *  closure widget prints one closure per metal from the one engine read rather
 *  than its retired typed seed rows. */
export function displayedClosureGapPct(balance: PeriodBalance, element: ChainElement): number {
  const F = roundMass(balance.masses.F);
  const C1 = roundMass(balance.masses.C1);
  const C2 = roundMass(balance.masses.C2);
  const T = roundMass(balance.masses.T);
  const headPb = roundGrade(balance.headGrade.Pb);
  const headZn = roundGrade(balance.headGrade.Zn);
  const pbConc = roundGrade(balance.productGrade.pbConcPct);
  const znConc = roundGrade(balance.productGrade.znConcPct);
  const znPb = roundGrade(balance.productGrade.znPbInZnConcPct);
  const tailsPb = roundGrade(balance.tailsAssay.tailsPbPct);
  const tailsZn = roundGrade(balance.tailsAssay.tailsZnPct);

  if (element === 'Pb') {
    const metalIn = F * (headPb / 100);
    const metalOut = C1 * (pbConc / 100) + C2 * (znPb / 100) + T * (tailsPb / 100);
    return metalIn > 0 ? ((metalOut - metalIn) / metalIn) * 100 : 0;
  }
  if (element === 'Zn') {
    const metalIn = F * (headZn / 100);
    const metalOut = C2 * (znConc / 100) + T * (tailsZn / 100);
    return metalIn > 0 ? ((metalOut - metalIn) / metalIn) * 100 : 0;
  }
  // Ag head grade is oz/t, not a percent — no /100 on the feed leg, and the
  // Ag-in-Zn-concentrate cross-assay the seed never carries is absent from the
  // product leg (the balance's own `metalOut.Ag`). F765 (#765): the product leg
  // is the **back-calculated** grade, not the reported assay — the closure is
  // the balance's own check, so the assayed grade never moves its gap.
  const headAg = roundGrade(balance.headGrade.Ag);
  const agConc = roundGrade(balance.closureAgConcOzPerT);
  const tailsAg = roundGrade(balance.tailsAssay.tailsAgOzPerT);
  const metalIn = F * headAg;
  const metalOut = C1 * agConc + T * tailsAg;
  return metalIn > 0 ? ((metalOut - metalIn) / metalIn) * 100 : 0;
}

export function metalInFeedForElement(
  element: ChainElement,
  period: 'day' | 'wtd' | 'mtd' = 'day',
): MeasuredQuantity {
  if (period === 'mtd' || period === 'wtd') {
    const window = period === 'mtd' ? mtdWindow() : wtdWindow();
    const ore = window.tonnes;
    const head = rangeMean(HEAD_GRADE_SERIES_ID[element], window.from, window.to);
    if (!Number.isFinite(ore) || ore <= 0 || !Number.isFinite(head)) return UNMEASURED;
    const value = element === 'Ag' ? ore * head : ore * (head / 100);
    return { value, unit: element === 'Ag' ? 'oz' : 'dst' };
  }
  const ore = readingByIdLookup('rd-kpi-ore-td');
  const head = readingByIdLookup(HEAD_GRADE_READING_ID[element]);
  if (ore?.value === undefined || head?.value === undefined) return UNMEASURED;
  const value = element === 'Ag' ? ore.value * head.value : ore.value * (head.value / 100);
  return { value, unit: element === 'Ag' ? 'oz' : 'dst/d' };
}

/** Mean of the measured points of `seriesId` inside the closed window
 *  `[from, to)` — the window-bounded twin of `windowMean`, which is open
 *  ended at the cursor. Only this module reads the series directly (A3 R6);
 *  `estimateContainedFromSeries` needs the upper bound so a closed month does
 *  not pull in the following month's points. */
function rangeMean(seriesId: string, from: SeriesTime, to: SeriesTime): number {
  const points = seriesAt(seriesId, INITIAL_SESSION_STATE).points.filter(
    (point): point is { at: SeriesTime; value: number } =>
      point.value !== null && diffMinutes(point.at, from) >= 0 && diffMinutes(point.at, to) < 0,
  );
  if (points.length === 0) return Number.NaN;
  return points.reduce((sum, point) => sum + point.value, 0) / points.length;
}

/** How a contained-metal estimate's measured window covers the period it
 *  closes — the `Period close` tab's provenance, named the way
 *  `oreWindowCoverage` names its window (K17 round 1 / #663). */
export type EstimateCoverage = {
  /** Measured daily ore points inside the window. */
  measuredDays: number;
  /** Days the period closes — the count the measured days are scaled to. */
  periodDays: number;
  /** Production days the measured points span, e.g. `2026-05-25` → `2026-05-31`. */
  span: { from: string; to: string };
  /** Daily ore stamps inside the window whose seed point is null. */
  missingDays: readonly SeriesTime[];
};

export type ContainedEstimate = {
  /** Whole-period contained metal: head × recovery, scaled to `periodDays`. */
  contained: number;
  /** How the measured window covers the period — derived, never typed. */
  coverage: EstimateCoverage;
};

/** Whole-period contained-metal estimate for a period close (K17 / #663).
 *
 *  The head-grade × recovery estimate must cover the whole period it closes.
 *  The seed's measured series starts 24 May, so a raw sum over May reads
 *  roughly half a month (and silently pulled in early June). This reads the
 *  measured ore points inside `[from, to)`, works out the mean measured day,
 *  and scales that rate to the period's own day count (`periodDays`) — the
 *  same coverage logic `oreWindowCoverage` uses for the WTD / MTD feed rate.
 *  Head and recovery are the window-bounded means of the same points.
 *
 *  Round 1 returns that coverage alongside the figure, so the May table can
 *  show the extrapolation as provenance derived from the estimate itself.
 *
 *  The caller computes the independent May figures in
 *  `scripts/probes/metal-balance-period-view-entry.ts` (summing the series
 *  itself, not through this function) so the two cannot drift together. */
export function estimateContainedFromSeries(
  from: SeriesTime,
  to: SeriesTime,
  periodDays: number,
  element: ChainElement,
): ContainedEstimate {
  const windowPoints = seriesAt(ORE_SERIES_ID, INITIAL_SESSION_STATE).points.filter(
    (point) => diffMinutes(point.at, from) >= 0 && diffMinutes(point.at, to) < 0,
  );
  const orePoints = windowPoints.filter(
    (point): point is { at: SeriesTime; value: number } => point.value !== null,
  );
  const missingDays = windowPoints.filter((point) => point.value === null).map((point) => point.at);
  const emptyCoverage: EstimateCoverage = {
    measuredDays: 0,
    periodDays,
    span: { from, to },
    missingDays,
  };
  if (orePoints.length === 0 || !Number.isFinite(periodDays) || periodDays <= 0) {
    return { contained: 0, coverage: emptyCoverage };
  }
  const ore = orePoints.reduce((sum, point) => sum + point.value, 0);
  // Scale the measured days to the whole period the close covers.
  const coverageFactor = periodDays / orePoints.length;
  const head = rangeMean(HEAD_GRADE_SERIES_ID[element], from, to);
  const recovery = rangeMean(RECOVERY_SERIES_ID[element], from, to);
  const first = orePoints[0]!;
  const last = orePoints[orePoints.length - 1]!;
  const coverage: EstimateCoverage = {
    measuredDays: orePoints.length,
    periodDays,
    span: { from: productionDayOf(first.at).date, to: productionDayOf(last.at).date },
    missingDays,
  };
  if (!Number.isFinite(ore) || !Number.isFinite(head) || !Number.isFinite(recovery)) {
    return { contained: 0, coverage };
  }
  const feedMetal = element === 'Ag' ? ore * head : ore * (head / 100);
  return { contained: feedMetal * coverageFactor * (recovery / 100), coverage };
}

export function stageOutcomeValue(readingId: string, period: AccountingPeriod): MeasuredQuantity {
  const reading = readingByIdLookup(readingId);
  if (!reading) return UNMEASURED;
  if (period === 'day') {
    return reading.value === undefined ? UNMEASURED : { value: reading.value, unit: reading.unit };
  }
  if (period === 'mtd') {
    const seriesId = STAGE_OUTCOME_SERIES_ID[readingId];
    if (!seriesId) return UNMEASURED;
    const mean = windowMean(seriesId, INITIAL_SESSION_STATE, mtdWindowStart());
    if (!Number.isFinite(mean)) return UNMEASURED;
    return { value: mean, unit: reading.unit };
  }
  return UNMEASURED;
}

// Seed-only readers — RECONCILED_VALUES and raw MEASUREMENT literals stay in
// this file (glass-poc-run A3 R6 / R13). Route transforms read PeriodView.

export function seedMeasurementRawValue(id: string): string {
  return MEASUREMENTS.find((entry) => entry.id === id)?.rawValue ?? '';
}

function leadingNumber(text: string): number | null {
  const match = text.match(/[\d,]+(?:\.\d+)?/);
  return match ? Number.parseFloat(match[0].replace(/,/g, '')) : null;
}

export function seedReconciledValue(id: string) {
  const value = RECONCILED_VALUES.find((entry) => entry.id === id);
  if (!value) throw new Error(`Missing reconciled value: ${id}`);
  return value;
}

export function circulatingLoadLabelFromSeed(): string {
  return seedReconciledValue('rv-circload').reconciledValue;
}

export function circulatingLoadAdjustmentFromSeed(): string {
  return seedReconciledValue('rv-circload').adjustment;
}

export function circulatingLoadSolidsStphFromSeed(): number {
  const fit = MEASUREMENTS.find((entry) => entry.id === 'ms-fit203');
  const dt = MEASUREMENTS.find((entry) => entry.id === 'ms-dt203');
  const q = leadingNumber(fit?.rawValue ?? '') ?? 0;
  const rhoSlurry = leadingNumber(dt?.rawValue ?? '') ?? 0;
  const rhoSolids = 3.4;
  if (q <= 0 || rhoSlurry <= 1) return 0;
  return q * ((rhoSlurry - 1) / (rhoSolids - 1)) * rhoSolids;
}
